import { type ReactNode, useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { createPortal } from 'react-dom'
import { styled } from 'styled-components'

import {
  Banner,
  Button,
  Link,
  Modal,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import type { DocumentDto } from '@b-stock/listing-api-client'
import { DocumentDtoDocTypeEnum } from '@b-stock/listing-api-client'

export const Description = styled.div`
  ${Typography.Body3};
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-weight: normal;
`

export const BidModal = styled(Modal)`
  border-radius: 0.5rem;

  @media ${Breakpoints.min.medium} {
    max-width: min(90vw, 43.75rem);
  }

  button {
    cursor: pointer;
  }
`

export const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  padding-right: 0.5rem;
`

// Mirrors fe-core's PageLoadingSpinner overlay, which is not exported from the
// package barrel and so cannot be reused directly.
const QuoteLoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 1000;
`

// Portals QuoteLoadingOverlay onto document.body so it centers on the
// viewport even when its React parent renders inside an ancestor with a
// CSS transform (e.g. an ag-grid virtualized .ag-row in List View) — a
// transformed ancestor establishes a new containing block for position:fixed
// descendants, which otherwise clips/mis-centers the overlay to that
// ancestor's bounding box instead of the viewport.
export const QuoteLoadingSpinner = () =>
  createPortal(
    <QuoteLoadingOverlay role="dialog" aria-hidden>
      <Spinner />
    </QuoteLoadingOverlay>,
    document.body
  )

export const StyledModalContent = styled(Modal.Content)`
  ${Typography.Body3}
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;

  @media ${Breakpoints.max.small} {
    padding: 0;
  }
`

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow-y: auto;

  @media ${Breakpoints.max.medium} {
    padding: 1.5rem;
  }

  @media ${Breakpoints.max.small} {
    padding: 1rem 3rem;
  }
`

export const BidDetailsContainer = styled.div``

export const BidDetailsHeader = styled.h4`
  ${Typography.Title3}
  margin: 0 0 0.375rem 0;
  display: grid;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  padding-bottom: 0.75rem;
`

export const MaxBidTitleAndBidAmount = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  @media ${Breakpoints.max.small} {
    font-size: 1.125rem;
  }
`

export const BidAmount = styled.span`
  color: ${Colors.Semantic.BStock.default.lightBackground};
`

export const BidDetailsContent = styled.div`
  display: grid;
  grid-gap: 2rem;
`

export const TimeLeftSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  color: ${Colors.Semantic.Neutral.black};
  ${Typography.Title3}
  font-weight: 700;

  svg {
    width: 1rem;
    height: 1rem;
    color: ${Colors.Semantic.Neutral.black};
  }
  @media ${Breakpoints.max.medium} {
    padding-bottom: 1rem;
  }
`

export const Checklist = styled.div`
  display: flex;
  width: 19.625rem;
  flex-direction: column;
  position: relative;
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};

  @media ${Breakpoints.max.small} {
    width: 100%;
  }
`

export const DetailsValue = styled.div`
  position: relative;
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const ActionContainer = styled(Modal.StyledActionContainer)`
  display: flex;
  justify-content: center;
  border: none;
  ${Shadows.Popovers}
  padding: 1rem;
  background: white;
  gap: 1rem;
  margin-top: auto;
  border-radius: 0 0 0.5rem 0.5rem;
  @media ${Breakpoints.max.medium} {
    padding: 1rem 2.5rem;
  }
  @media ${Breakpoints.max.small} {
    display: grid;
  }
`

const StyledActionButton = styled(Button)`
  margin-left: 0;
`

export const StyledButton = styled(StyledActionButton)`
  white-space: nowrap;
  order: 2;
  @media ${Breakpoints.max.small} {
    width: 100%;
    order: 1;
  }
`

export const StyledCancelButton = styled(StyledActionButton)`
  white-space: nowrap;
  order: 1;
  @media ${Breakpoints.max.small} {
    width: 100%;
    order: 2;
  }
`

/**
 * The design's footer buttons: equal width, Cancel outlined beside a solid
 * Confirm, rather than a small link next to a small button.
 *
 * Scoped to Seller Collects by the caller — every other modal keeps the
 * shared sizing, which other surfaces depend on.
 */
export const ButtonRow = styled.div<{ $even?: boolean }>`
  display: contents;

  ${({ $even }) =>
    $even &&
    `
    display: flex;
    gap: 0.875rem;
    width: 100%;

    > * {
      flex: 1;
    }
  `}
`

export const StyledBanner = styled(Banner)`
  padding: 1.5rem;
  margin-bottom: 0.1875rem;
`

export const SlimBanner = styled(Banner)`
  padding: 0.75rem 1rem;
  margin-bottom: 0.1875rem;
`

export const Divider = styled.div`
  width: 100%;
  border-bottom: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  margin-bottom: 1rem;
`

/**
 * The terms-of-purchase chunk both confirmation modals drop into their footer
 * message. Shared so the bid and Buy Now footers cannot drift: each picks its
 * own message, but the link inside them is one piece of code.
 *
 * The `#` fallback is the behaviour these footers already had, kept so an
 * ordinary listing with no terms document renders exactly as before.
 */
export const useTermsOfPurchaseLink = (documents?: DocumentDto[]) =>
  useMemo(() => {
    const url = documents?.find(
      ({ docType }) => docType === DocumentDtoDocTypeEnum.TermsOfService
    )?.url
    const TermsOfPurchaseLink = (chunks: ReactNode) => (
      <Link target="_blank" href={url ?? '#'}>
        {chunks}
      </Link>
    )
    return TermsOfPurchaseLink
  }, [documents])
